import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, AlertTriangle, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const CoverageGrid = ({ coverage }) => {
  const dot = (has, future, closed) => {
    if (closed) return { bg: '#E7DFD1', title: 'closed' };
    if (future) return { bg: '#F1EBE0', title: 'upcoming' };
    return has ? { bg: '#3F7A4E', title: 'has data' } : { bg: '#D98C6A', title: 'no data' };
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {coverage.days.map((d) => {
          const b = dot(d.booker, d.is_future, d.is_closed);
          const h = dot(d.homebase, d.is_future, d.is_closed);
          return (
            <div key={d.date} className="flex flex-col items-center w-10" data-testid={`coverage-day-${d.date}`}>
              <div className="text-[10px] text-stone-500">{d.dow}</div>
              <div className="text-[10px] text-stone-700 mb-1">{d.date.slice(5)}</div>
              <div className="flex flex-col gap-1">
                <span className="w-6 h-3 rounded-sm" style={{ background: b.bg }} title={`Booker · ${b.title}`} data-testid={`coverage-booker-${d.date}`} />
                <span className="w-6 h-3 rounded-sm" style={{ background: h.bg }} title={`Homebase · ${h.title}`} data-testid={`coverage-homebase-${d.date}`} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-stone-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#3F7A4E' }} /> has data</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#D98C6A' }} /> gap (no data)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#E7DFD1' }} /> closed (Sun)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#F1EBE0' }} /> upcoming</span>
        <span className="ml-2">Top row = Booker · bottom = Homebase</span>
      </div>
    </div>
  );
};

export default function ImportHubPage() {
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [covLoading, setCovLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHub();
  }, []);

  const fetchHub = async () => {
    try {
      const res = await fetch(`${API}/api/admin/import/hub`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMatrix(data.periods);
      }
    } catch (err) {
      console.error('Failed to load import hub:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCoverage = async (periodId) => {
    if (expandedId === periodId) { setExpandedId(null); setCoverage(null); return; }
    setExpandedId(periodId); setCoverage(null); setCovLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/import/coverage/${periodId}`, { credentials: 'include' });
      if (res.ok) setCoverage(await res.json());
    } catch (err) {
      console.error('Failed to load coverage:', err);
    } finally {
      setCovLoading(false);
    }
  };

  const filtered = matrix.filter(p => {
    if (filter === 'open') return p.status !== 'finalized' && p.status !== 'locked';
    if (filter === '2025') return p.start_date && p.start_date.startsWith('2025');
    if (filter === '2026') return p.start_date && p.start_date.startsWith('2026');
    return true;
  });

  const SourceCell = ({ source, lastUpdated, label }) => {
    const stamp = lastUpdated
      ? formatDate(lastUpdated, { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
    return (
      <div
        className="rounded-md bg-[#F7F2E8] border border-[#EADFCB] px-2 py-1.5 text-center"
        data-testid={`import-cell-${label.toLowerCase()}`}
      >
        {source ? (
          <div className="flex items-center justify-center gap-1 text-green-700">
            <CheckCircle className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">{source.rows_written}</span>
          </div>
        ) : null}
        <div className="text-[11px] mt-0.5 leading-tight">
          {stamp ? (
            <span className="text-stone-500">Updated {stamp}</span>
          ) : (
            <span className="text-stone-300" data-testid={`import-never-${label.toLowerCase()}`}>Never</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-serif font-bold text-stone-900">Import Hub</h1>
            <p className="text-stone-600 mt-2">Period by source coverage matrix</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/import/homebase')}>
              <Upload className="mr-2 h-4 w-4" />
              Homebase
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/import/booker')}>
              <Upload className="mr-2 h-4 w-4" />
              Booker
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/import/adp')}>
              <Upload className="mr-2 h-4 w-4" />
              ADP
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/import/text')} data-testid="import-hub-paste-btn">
              <Upload className="mr-2 h-4 w-4" />
              Paste text
            </Button>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All Periods
          </Button>
          <Button
            variant={filter === 'open' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('open')}
          >
            Open Only
          </Button>
          <Button
            variant={filter === '2025' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('2025')}
          >
            2025
          </Button>
          <Button
            variant={filter === '2026' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('2026')}
          >
            2026
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-100">
                      <th className="px-4 py-3 text-left text-sm font-medium text-stone-700">Period</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-stone-700">Dates</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-stone-700">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-stone-700">Homebase</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-stone-700">Booker</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-stone-700">ADP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((period) => {
                      const isOpen = period.status !== 'finalized' && period.status !== 'locked';
                      const pid = period._id;
                      return (
                      <React.Fragment key={pid || `${period.start_date}-${period.end_date}`}>
                      <tr className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="px-4 py-3 text-sm">{period.period_number || '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {period.start_date} to {period.end_date}
                          {isOpen && pid && (
                            <button
                              onClick={() => toggleCoverage(pid)}
                              data-testid={`coverage-toggle-${pid}`}
                              className="ml-2 text-[11px] text-amber-700 underline underline-offset-2 hover:text-amber-900"
                            >
                              {expandedId === pid ? 'hide days' : 'coverage by day'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                              period.is_locked
                                ? 'bg-red-100 text-red-800'
                                : period.status === 'finalized'
                                ? 'bg-orange-100 text-orange-800'
                                : period.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {period.is_locked ? 'locked' : period.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <SourceCell source={period.homebase} lastUpdated={period.last_updated?.homebase} label="Homebase" />
                        </td>
                        <td className="px-4 py-3">
                          <SourceCell source={period.booker} lastUpdated={period.last_updated?.booker} label="Booker" />
                        </td>
                        <td className="px-4 py-3">
                          <SourceCell source={period.adp} lastUpdated={period.last_updated?.adp} label="ADP" />
                        </td>
                      </tr>
                      {expandedId === pid && (
                        <tr className="bg-[#FBF7EF] border-b border-stone-100" data-testid={`coverage-grid-${pid}`}>
                          <td colSpan={6} className="px-4 py-3">
                            {covLoading || !coverage ? (
                              <div className="text-sm text-stone-500">Loading coverage…</div>
                            ) : (
                              <CoverageGrid coverage={coverage} />
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-sm text-stone-600 space-y-1">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-700" />
            <span>Rows imported this session · "Updated {'{date}'}" shows the last refresh for that source</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-stone-300">Never</span>
            <span>No data imported yet for this source</span>
          </div>
        </div>
      </div>
    </div>
  );
}
